import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Avatar,
  Box,
  Paper,
  Tabs,
  Tab,
  Badge,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Article as ArticleIcon,
  Payment as PaymentIcon,
  Campaign as CampaignIcon,
  Public as PublicIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Circle as CircleIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  Mail as MailIcon,
  MailOutline as MailOutlineIcon,
} from '@mui/icons-material';
import notificationApi, { Notification, NotificationPreferences } from '../../services/notificationApi';

const Notifications: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState<NotificationPreferences>({
    emailNotifications: true,
    pushNotifications: true,
    prUpdates: true,
    paymentAlerts: true,
    campaignUpdates: true,
    systemAlerts: true,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await notificationApi.getUserNotifications(1, 50, false);
      
      if (response.success) {
        setNotifications(response.notifications);
        setFilteredNotifications(response.notifications);
      }
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch notification preferences
  const fetchPreferences = useCallback(async () => {
    try {
      const response = await notificationApi.getPreferences();
      if (response.success) {
        setNotificationSettings(response.preferences);
      }
    } catch (err: any) {
      console.error('Failed to fetch preferences:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchPreferences();
    }
  }, [user, fetchNotifications, fetchPreferences]);

  // Filter notifications based on tab and search
  useEffect(() => {
    let filtered = notifications;

    // Filter by read/unread
    if (selectedTab === 1) {
      filtered = filtered.filter((n) => !n.read);
    } else if (selectedTab === 2) {
      filtered = filtered.filter((n) => n.read);
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter((n) => n.type === filterType);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          n.message.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredNotifications(filtered);
  }, [selectedTab, filterType, searchTerm, notifications]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'pr_published':
        return <CheckCircleIcon style={{ color: '#4caf50' }} />;
      case 'pr_rejected':
        return <ErrorIcon style={{ color: '#f44336' }} />;
      case 'payment':
        return <PaymentIcon style={{ color: '#0927eb' }} />;
      case 'campaign':
        return <CampaignIcon style={{ color: '#0927eb' }} />;
      case 'success':
        return <CheckCircleIcon style={{ color: '#4caf50' }} />;
      case 'warning':
        return <WarningIcon style={{ color: '#ff5200' }} />;
      case 'info':
        return <InfoIcon style={{ color: '#2196f3' }} />;
      case 'system':
        return <SettingsIcon style={{ color: '#cdcdcd' }} />;
      default:
        return <NotificationsIcon style={{ color: '#666' }} />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'pr_published':
      case 'success':
        return '#4caf50'; // Green for success
      case 'pr_rejected':
        return '#f44336'; // Red for errors
      case 'payment':
      case 'campaign':
        return '#0927eb'; // Primary blue (matches website)
      case 'warning':
        return '#ff5200'; // Orange (matches website secondary color)
      case 'info':
        return '#2196f3'; // Light blue for info
      case 'system':
        return '#cdcdcd'; // Gray for system
      default:
        return '#666';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return timestamp.toLocaleDateString();
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationApi.markAsRead(id);
      setNotifications(
        notifications.map((n) => (n.id === id ? { ...n, read: true, readAt: new Date() } : n))
      );
      setSuccessMessage('Notification marked as read');
    } catch (err: any) {
      setError(err.message || 'Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationApi.markAllAsRead();
      setNotifications(notifications.map((n) => ({ ...n, read: true, readAt: new Date() })));
      setSuccessMessage(`${response.updatedCount} notifications marked as read`);
    } catch (err: any) {
      setError(err.message || 'Failed to mark all notifications as read');
    }
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      await notificationApi.deleteNotification(id);
      setNotifications(notifications.filter((n) => n.id !== id));
      setAnchorEl(null);
      setSuccessMessage('Notification deleted successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to delete notification');
    }
  };

  const handleRefresh = () => {
    fetchNotifications(true);
  };

  const handleSaveSettings = async () => {
    try {
      const response = await notificationApi.updatePreferences(notificationSettings);
      if (response.success) {
        setSuccessMessage('Notification preferences saved successfully');
        setSettingsDialogOpen(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save notification preferences');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    setSelectedNotification(notification);
    setDetailDialogOpen(true);
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, notification: Notification) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const stats = [
    {
      label: 'Total',
      value: notifications.length,
      icon: <NotificationsIcon />,
      color: '#0927eb',
    },
    {
      label: 'Unread',
      value: unreadCount,
      icon: <MailIcon />,
      color: '#ff5200',
    },
    {
      label: 'Today',
      value: notifications.filter((n) => {
        const today = new Date();
        const notifDate = new Date(n.timestamp);
        return notifDate.toDateString() === today.toDateString();
      }).length,
      icon: <AccessTimeIcon />,
      color: '#cdcdcd',
    },
    {
      label: 'Important',
      value: notifications.filter((n) => n.type === 'pr_published' || n.type === 'payment').length,
      icon: <TrendingUpIcon />,
      color: '#0927eb',
    },
  ];

  return (
    <Container maxWidth="xl" style={{ padding: '24px' }}>
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box style={{ marginBottom: '32px' }}>
        <Typography
          variant="h4"
          style={{
            fontWeight: 'bold',
            color: '#0927eb',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon style={{ fontSize: '32px' }} />
          </Badge>
          Notifications
        </Typography>
        <Typography variant="body1" style={{ color: '#666' }}>
          Stay updated with your press releases, payments, and campaigns
        </Typography>
      </Box>

      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <CircularProgress style={{ color: '#0927eb' }} />
        </Box>
      ) : (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} style={{ marginBottom: '32px' }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={6} md={3} key={index}>
                <Card
                  style={{
                    borderRadius: '12px',
                    background: stat.color,
                    color: stat.color === '#cdcdcd' ? '#000' : 'white',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>
                          {stat.label}
                        </Typography>
                      </div>
                      <div style={{ opacity: 0.3, fontSize: '40px' }}>{stat.icon}</div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Filters and Actions */}
          <Paper style={{ padding: '24px', marginBottom: '24px', borderRadius: '12px' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Type</InputLabel>
                  <Select
                    value={filterType}
                    onChange={(e: SelectChangeEvent) => setFilterType(e.target.value)}
                    label="Filter by Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="pr_published">PR Published</MenuItem>
                    <MenuItem value="payment">Payments</MenuItem>
                    <MenuItem value="campaign">Campaigns</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="warning">Warnings</MenuItem>
                    <MenuItem value="info">Information</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                    style={{ borderColor: '#0927eb', color: '#0927eb' }}
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DoneAllIcon />}
                    style={{ borderColor: '#0927eb', color: '#0927eb' }}
                    onClick={handleMarkAllAsRead}
                    disabled={unreadCount === 0}
                  >
                    Mark All Read
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    style={{ backgroundColor: '#000000', color: 'white' }}
                    onClick={() => setSettingsDialogOpen(true)}
                  >
                    Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

      {/* Tabs */}
      <Paper style={{ marginBottom: '24px', borderRadius: '12px' }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
            },
            '& .Mui-selected': {
              color: '#0927eb',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0927eb',
            },
          }}
        >
          <Tab
            label={
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <NotificationsIcon />
                All ({notifications.length})
              </Box>
            }
          />
          <Tab
            label={
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Badge badgeContent={unreadCount} color="error">
                  <MailIcon />
                </Badge>
                Unread ({unreadCount})
              </Box>
            }
          />
          <Tab
            label={
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MailOutlineIcon />
                Read ({notifications.length - unreadCount})
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <Paper style={{ padding: '48px', textAlign: 'center', borderRadius: '12px' }}>
          <NotificationsIcon style={{ fontSize: '64px', color: '#cdcdcd', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ color: '#666', marginBottom: '8px' }}>
            No notifications found
          </Typography>
          <Typography variant="body2" style={{ color: '#999' }}>
            {searchTerm || filterType !== 'all'
              ? 'Try adjusting your filters'
              : "You're all caught up!"}
          </Typography>
        </Paper>
      ) : (
        <Card style={{ borderRadius: '12px' }}>
          <List>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: notification.read ? 'transparent' : 'rgba(9, 39, 235, 0.05)',
                    transition: 'all 0.3s ease',
                    borderLeft: `4px solid ${notification.read ? 'transparent' : getNotificationColor(notification.type)}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(9, 39, 235, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = notification.read
                      ? 'transparent'
                      : 'rgba(9, 39, 235, 0.05)';
                  }}
                >
                  <ListItemIcon>
                    <Avatar
                      style={{
                        backgroundColor: notification.read ? '#f5f5f5' : 'rgba(9, 39, 235, 0.1)',
                        width: '48px',
                        height: '48px',
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: notification.read ? 500 : 700, flex: 1 }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.read && (
                          <CircleIcon style={{ fontSize: '10px', color: '#0927eb' }} />
                        )}
                        <Typography variant="caption" style={{ color: '#666' }}>
                          {formatTimestamp(notification.timestamp)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" style={{ color: '#666' }}>
                        {notification.message}
                      </Typography>
                    }
                  />
                  <IconButton
                    onClick={(e) => handleMenuClick(e, notification)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </ListItem>
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      {/* Notification Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {selectedNotification && !selectedNotification.read && (
          <MenuItem
            onClick={() => {
              if (selectedNotification) {
                handleMarkAsRead(selectedNotification.id);
              }
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <DoneAllIcon fontSize="small" />
            </ListItemIcon>
            Mark as Read
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (selectedNotification) {
              handleDeleteNotification(selectedNotification.id);
            }
          }}
          style={{ color: '#f44336' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" style={{ color: '#f44336' }} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Notification Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedNotification && (
          <>
            <DialogTitle>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {getNotificationIcon(selectedNotification.type)}
                <Typography variant="h6" style={{ fontWeight: 'bold', flex: 1 }}>
                  {selectedNotification.title}
                </Typography>
                <Chip
                  label={selectedNotification.read ? 'Read' : 'Unread'}
                  size="small"
                  style={{
                    backgroundColor: selectedNotification.read ? '#cdcdcd' : '#0927eb',
                    color: selectedNotification.read ? '#000' : 'white',
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box style={{ marginBottom: '16px' }}>
                <Typography variant="body2" style={{ color: '#666', marginBottom: '8px' }}>
                  <AccessTimeIcon style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '4px' }} />
                  {new Date(selectedNotification.timestamp).toLocaleString()}
                </Typography>
              </Box>
              <Divider style={{ marginBottom: '16px' }} />
              <Typography variant="body1" style={{ marginBottom: '16px' }}>
                {selectedNotification.message}
              </Typography>
              {selectedNotification.metadata && (
                <Paper style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Typography variant="caption" style={{ color: '#666', fontWeight: 600 }}>
                    DETAILS
                  </Typography>
                  <Box style={{ marginTop: '8px' }}>
                    {selectedNotification.metadata.prId && (
                      <Typography variant="body2">PR ID: {selectedNotification.metadata.prId}</Typography>
                    )}
                    {selectedNotification.metadata.paymentAmount && (
                      <Typography variant="body2">
                        Amount: ${selectedNotification.metadata.paymentAmount}
                      </Typography>
                    )}
                    {selectedNotification.metadata.campaignId && (
                      <Typography variant="body2">
                        Campaign ID: {selectedNotification.metadata.campaignId}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
              {selectedNotification.actionUrl && (
                <Button
                  variant="contained"
                  style={{ backgroundColor: '#0927eb', color: 'white' }}
                  onClick={() => {
                    window.location.href = selectedNotification.actionUrl || '#';
                  }}
                >
                  View Details
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SettingsIcon />
            Notification Settings
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: '#666', marginBottom: '24px' }}>
            Choose which notifications you want to receive
          </Typography>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.emailNotifications}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      emailNotifications: e.target.checked,
                    })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0927eb',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0927eb',
                    },
                  }}
                />
              }
              label="Email Notifications"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.pushNotifications}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      pushNotifications: e.target.checked,
                    })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0927eb',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0927eb',
                    },
                  }}
                />
              }
              label="Push Notifications"
            />
          </Box>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '12px' }}>
            Notification Types
          </Typography>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.prUpdates}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      prUpdates: e.target.checked,
                    })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0927eb',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0927eb',
                    },
                  }}
                />
              }
              label="PR Updates"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.paymentAlerts}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      paymentAlerts: e.target.checked,
                    })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0927eb',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0927eb',
                    },
                  }}
                />
              }
              label="Payment Alerts"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.campaignUpdates}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      campaignUpdates: e.target.checked,
                    })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0927eb',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0927eb',
                    },
                  }}
                />
              }
              label="Campaign Updates"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.systemAlerts}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      systemAlerts: e.target.checked,
                    })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0927eb',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0927eb',
                    },
                  }}
                />
              }
              label="System Alerts"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#0927eb', color: 'white' }}
            onClick={handleSaveSettings}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Container>
  );
};

export default Notifications;



